
import { useState } from "react";
import { useToast } from "@/components/ui/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Camera, Trash, Loader2 } from "lucide-react";
import { v4 as uuidv4 } from "uuid";

interface ProfilePictureUploaderProps {
  user: any;
  profile: any;
  onProfileUpdate: (profile: any) => void;
}

export const ProfilePictureUploader = ({
  user,
  profile,
  onProfileUpdate,
}: ProfilePictureUploaderProps) => {
  const [uploading, setUploading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const { toast } = useToast();

  const uploadAvatar = async (event: React.ChangeEvent<HTMLInputElement>) => {
    try {
      setUploading(true);
      
      if (!event.target.files || event.target.files.length === 0) {
        throw new Error("You must select an image to upload.");
      }

      const file = event.target.files[0];
      const fileExt = file.name.split(".").pop();
      const filePath = `${user.id}/${uuidv4()}.${fileExt}`;

      // Upload the file to Supabase storage
      const { error: uploadError } = await supabase.storage
        .from("avatars")
        .upload(filePath, file);

      if (uploadError) {
        throw uploadError;
      }

      // Get the public URL
      const { data: publicUrlData } = supabase.storage
        .from("avatars")
        .getPublicUrl(filePath);

      if (!publicUrlData.publicUrl) {
        throw new Error("Failed to get public URL for uploaded image");
      }

      // Update the user profile with the new avatar URL
      const { data, error } = await supabase
        .from("profiles")
        .update({ avatar_url: publicUrlData.publicUrl })
        .eq("id", user.id)
        .select()
        .single();

      if (error) {
        throw error;
      }

      onProfileUpdate(data);
      
      toast({
        title: "Success!",
        description: "Your profile picture has been updated.",
      });
    } catch (error: any) {
      console.error("Error uploading avatar:", error.message);
      toast({
        title: "Error uploading avatar",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setUploading(false);
      // Reset file input
      const fileInput = document.getElementById("avatar") as HTMLInputElement;
      if (fileInput) fileInput.value = "";
    }
  };

  const removeAvatar = async () => {
    try {
      setDeleting(true);
      
      if (!profile?.avatar_url) {
        return;
      }

      // Extract file path from the URL
      const avatarPath = profile.avatar_url.split("/").slice(-2).join("/");
      
      // Delete the file from storage
      const { error: deleteError } = await supabase.storage
        .from("avatars")
        .remove([avatarPath]);

      if (deleteError) {
        throw deleteError;
      }

      // Update the user profile to remove the avatar URL
      const { data, error } = await supabase
        .from("profiles")
        .update({ avatar_url: null })
        .eq("id", user.id)
        .select()
        .single();

      if (error) {
        throw error;
      }

      onProfileUpdate(data);
      
      toast({
        title: "Success!",
        description: "Your profile picture has been removed.",
      });
    } catch (error: any) {
      console.error("Error removing avatar:", error.message);
      toast({
        title: "Error removing avatar",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setDeleting(false);
    }
  };

  // Get initials for avatar fallback
  const getInitials = () => {
    const email = user?.email || "";
    return email.charAt(0).toUpperCase();
  };

  return (
    <div className="flex flex-col items-center sm:flex-row sm:items-start sm:space-x-8">
      <div className="mb-4 sm:mb-0">
        <Avatar className="w-24 h-24 sm:w-32 sm:h-32 border-2 border-primary/20">
          <AvatarImage src={profile?.avatar_url} alt="Profile" />
          <AvatarFallback className="text-lg sm:text-2xl bg-primary/10">
            {getInitials()}
          </AvatarFallback>
        </Avatar>
      </div>
      
      <div className="space-y-4 text-center sm:text-left">
        <h3 className="text-base font-medium">Profile Picture</h3>
        <p className="text-sm text-muted-foreground mb-4">
          Upload a profile picture to personalize your account.
          <br />
          Recommended size: 400x400 pixels.
        </p>
        
        <div className="flex flex-wrap gap-3 justify-center sm:justify-start">
          <Button
            type="button"
            variant="outline"
            onClick={() => document.getElementById("avatar")?.click()}
            disabled={uploading}
            className="relative"
          >
            {uploading ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Uploading...
              </>
            ) : (
              <>
                <Camera className="h-4 w-4 mr-2" />
                Upload Photo
              </>
            )}
            <input
              type="file"
              id="avatar"
              accept="image/*"
              onChange={uploadAvatar}
              disabled={uploading}
              className="sr-only"
            />
          </Button>
          
          {profile?.avatar_url && (
            <Button
              type="button"
              variant="destructive"
              onClick={removeAvatar}
              disabled={deleting}
            >
              {deleting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Removing...
                </>
              ) : (
                <>
                  <Trash className="h-4 w-4 mr-2" />
                  Remove
                </>
              )}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};
